import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { Redirect, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import * as Screens from "src/screens";
import { SELECT_COMPONENT_TYPES } from "src/api/Select";
import { runInAction } from "mobx";
import { STEP_TYPES } from "src/api/Processes";
import { Step } from "./Step";
import { Tiles } from "src/components/tile";
import { SelectOther } from "./SelectOther";
import { useTranslation } from "react-i18next";
import { Route, selectRoute } from "src/config/routes";
import { getLang } from "src/utils/i18n";

const SelectComponentListBase = () => {
    const { t } = useTranslation();
    const { navStore, easyContactStore } = useContext(RootStoreContext);
    const { processId, stepId, componentId } = useParams<{ processId: string; stepId: string; componentId: string }>();
    const { nextStep } = easyContactStore.stepNavigation;
    const component = easyContactStore.getSelectComponentById(componentId);
    const { prevComponentId } = easyContactStore.componentNavigation;

    const route = selectRoute(Route.step, { processId, stepType: String(STEP_TYPES.SELECT), stepId });

    useEffect(() => {
        runInAction(() => {
            const backPath = prevComponentId
                ? selectRoute(Route.selectComponent, {
                      processId,
                      stepType: String(STEP_TYPES.SELECT),
                      stepId,
                      componentId: prevComponentId
                  })
                : route;
            navStore.setBackbutton(backPath, t("labels.goback"));
        });
    }, [prevComponentId, navStore, processId, stepId, route, t]);

    if (!component) {
        return <Redirect to={route} />;
    }

    if (component.type === SELECT_COMPONENT_TYPES.COMPONENT) {
        if (nextStep) {
            easyContactStore.setComponent(
                componentId,
                component.component_mls.find((component) => component.language === getLang())?.title ?? ""
            );

            //Go to next Step
            return (
                <Redirect
                    to={selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })}
                />
            );
        } else {
            return <Screens.Error message={t("error.nodata")} />;
        }
    }

    if (component.type !== SELECT_COMPONENT_TYPES.OTHER) {
        if (component.parameters?.roomtype) {
            easyContactStore.setRoomType(component.parameters?.roomtype);
        }
        if (component.parameters?.appliancetype) {
            easyContactStore.setApplianceType(component.parameters?.appliancetype);
        }
    }

    return (
        <Step>
            {component.type === SELECT_COMPONENT_TYPES.OTHER ? (
                <SelectOther component={component} />
            ) : (
                <UI.Container className="pt-4 flex-grow-1 d-flex flex-column justify-content-start">
                    <UI.Row>
                        <UI.Col lg={{ span: 8, offset: 2 }} sm={{ span: 10, offset: 1 }}>
                            <Tiles.Container>
                                {component.components?.map((component, index) => {
                                    const label = component.component_mls.find(
                                        (component) => component.language === getLang()
                                    )?.title;

                                    return (
                                        <Tiles.Tile
                                            key={component.id}
                                            id={component.id}
                                            icon={component.icon}
                                            label={label ?? ""}
                                            index={index}
                                            to={selectRoute(Route.selectComponent, {
                                                processId,
                                                stepType: String(STEP_TYPES.SELECT),
                                                stepId,
                                                componentId: component.id
                                            })}
                                        />
                                    );
                                })}
                            </Tiles.Container>
                        </UI.Col>
                    </UI.Row>
                </UI.Container>
            )}
        </Step>
    );
};

export const SelectComponentList = Sentry.withProfiler(observer(SelectComponentListBase));
